/**
 * The quote / sales-order wire. Phase 3a · U5.
 *
 * ===========================================================================
 * EVERY MONEY FIELD ON EVERY TYPE IN THIS FILE IS `string`, AND IT IS NEVER
 * PARSED
 * ===========================================================================
 * `qty`, `unit_rate`, `discount_pct`, `line_total`, `subtotal`,
 * `discount_total`, `tax_total`, `taxable_base`, `grand_total`, `rate`,
 * `base_amount`, `tax_amount` all cross the wire as DECIMAL literals in strings
 * (`apps/api/src/modules/quotes/quotes.service.ts` — "MONEY AND QUANTITIES ARE
 * STRINGS ON THE WIRE").
 *
 * They stay strings HERE. There is no `Number()` in this module applied to a
 * money value, no arithmetic, and no derived total. The unit's whole point is
 * that the server prices and the client renders — so the client must not even be
 * ABLE to disagree, and the cheapest way to guarantee that is to never convert
 * the value into something you could add.
 *
 * The one place a money string becomes a number is `formatMoney`
 * (`features/inquiries/format.ts`), for `Intl.NumberFormat` — a PRESENTATION
 * conversion of one already-final value, which is imported rather than re-written
 * so this app has exactly one spelling of an amount.
 *
 * ===========================================================================
 * WHAT IS NOT HERE, AND WHY
 * ===========================================================================
 *  · NO client-side TOTAL, ever, and no client-side price LOOKUP. `unit_rate` is
 *    typed by the operator (Phase 3a close-out, T0) or defaulted by the server
 *    from `country_products.price`; this module fetches the country catalogue
 *    (below) so a PICKER can list what is sellable and prefill the rate field.
 *    That prefilled figure is an INPUT the operator can change, and it reaches a
 *    total only by being sent back and multiplied on the server.
 *  · NO read route for discount requests. There is none in the API (OQ-8), so
 *    what a screen can know about a discount is exactly what the two POSTs
 *    returned in this session. `features/quotes/QuoteRecordPage.tsx` says so on
 *    screen rather than implying a list exists.
 *
 * Every call goes through `apiRequest` / `fetchBinary`, which own the
 * single-flight refresh mutex (D-web-1). ESLint forbids `fetch` outside
 * `src/lib/api.ts` for exactly that reason.
 */

import { apiRequest, fetchBinary } from '../../lib/api';
import type { BinaryResponse } from '../../lib/api';
import type {
  DiscountRequestStatus,
  QuoteStatus,
  SalesOrderStatus,
} from '../../domain/quotes';

/* ─────────────────────────────────────────────────────────────────── paging ── */

/** The API's envelope for every list route (`countries/support/http.ts`). */
export interface Paged<T> {
  data: T[];
  page: number;
  perPage: number;
  total: number;
}

/* ───────────────────────────────────────────────────────────────── the wire ── */

/**
 * `QuoteDto` — transcribed from `apps/api/src/modules/quotes/quotes.service.ts:237`.
 *
 * `fx_rate_to_inr` and `fx_stale` are D2's outputs and are NOT rendered on any
 * customer-facing surface; `fx_stale` IS rendered on the record, because a total
 * computed from an old rate is a fact the person quoting should know.
 */
/**
 * `'crf' | 'tally_sales' | 'mixed'` — mirrors `QUOTE_TYPES` in the API's entity barrel,
 * and `domain/quotes.test.ts` reads that file's source to prove the two agree.
 *
 * `mixed` (migration 47, owner ruling Q3) is a document carrying both a Tally licence and
 * customisation work. It was mirrored here BEFORE anything could produce it, so that the
 * places treating two values as exhaustive (`QUOTE_TYPE_LABELS`, `QuoteTypeTag`,
 * `missingPatchReason`) failed to compile then rather than rendering wrongly later.
 *
 * ⚠ THE SERVER PRODUCES IT NOW (C11/IQ-12b). `quotes.type` is DERIVED from the document's
 * lines — any Tally licence line beside any other line is `mixed`, all licence lines is
 * `tally_sales`, no licence line is `crf` — it floats while the document is a draft and
 * freezes when `sent_at` is stamped.
 *
 * ⚠ AND THERE IS NO CREATE PICKER ANY MORE. The builder used to offer a radio; it now
 * DISPLAYS the computed type and sends nothing. The API still accepts `type` on create,
 * but only as an assertion it checks against the lines, and only from the shorter
 * `CLIENT_SETTABLE_QUOTE_TYPES` — `mixed` is not a value any client may state.
 */
export const QUOTE_TYPES = ['crf', 'tally_sales', 'mixed'] as const;
export type QuoteType = (typeof QUOTE_TYPES)[number];

export interface QuoteDto {
  id: string;
  doc_no: string;
  /**
   * WHICH KIND OF DOCUMENT THIS IS (migration 14, §B1; `mixed` from migration 47).
   *
   * The brief is explicit that the web must "reuse the CRF quote components and branch
   * on type — do NOT fork a second quote UI", so this field is the branch. Everything
   * downstream (labels, the patch requirement, what a confirm produces) keys off it.
   *
   * ⚠ THE SENTENCE THAT USED TO END THIS PARAGRAPH IS GONE, AND ITS GOING IS THE POINT
   * (C11/IQ-12b). It read: *"and nothing infers it from the product mix — a mixed quote
   * would infer wrong."* Both halves were answered rather than argued with: `mixed` is a
   * real value now, so the inference has somewhere to put such a document, and the SERVER
   * is what infers — from `products.kind`, which only a group admin may set. This field is
   * still read and never computed by this app.
   */
  type: QuoteType;
  legacy_ref: string | null;
  country_id: number;
  inquiry_id: string | null;
  party_id: string;
  party_name: string | null;
  /**
   * §B3 — the CUSTOMER's country. NULL for the five domestic scopes (it would repeat
   * the document's own country); non-null for XM, where it is the only thing separating
   * a Kenyan customer from an Omani one. Rendered by `customerOriginLabel`.
   */
  party_customer_country?: string | null;
  status: QuoteStatus;
  currency: string;
  fx_rate_to_inr: string;
  fx_stale: boolean;
  subtotal: string;
  discount_total: string;
  tax_total: string;
  grand_total: string;
  /**
   * WHICH NAMED TAX CONFIGURATION priced this document (migration 30, OD-1 — design
   * U3): the provenance id and its display label, joined live server-side. Both null
   * on documents priced before the cutover — that is a fact, and the record renders
   * it as one rather than inventing a group nobody picked. Neither is money.
   */
  tax_group_id: string | null;
  tax_group_name: string | null;
  valid_till: string | null;
  pdf_path: string | null;
  sent_at: string | null;
  confirmed_at: string | null;
  /**
   * A RECOVERY document (AGT U7): `M` manual, `D` duplicate, and the ORIGINAL's série/number.
   * The server always sends both (null on every other quote); optional here so an absent key
   * reads as "not a recovery document", as the `party_customer_country` precedent does.
   */
  recovery_kind?: 'M' | 'D' | null;
  recovery_ref?: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** `QuoteLineRowDto` — `quotes.service.ts:206`. */
export interface QuoteLineRowDto {
  id: string;
  quote_id: string;
  country_id: number;
  line_no: number;
  product_id: string;
  product_code: string | null;
  description: string;
  qty: string;
  users: number | null;
  months: number | null;
  /** The rate the line was priced at — the operator's, or the price list's. */
  unit_rate: string;
  discount_pct: string;
  /**
   * The renewal term recorded on this line, or null for a product not sold on a term.
   *
   * SERVER-DECIDED, and echoed here so the builder can show the operator the prefill it
   * is about to store. Neither field is money; both are recorded in the API's
   * money-field audit as deliberate non-entries.
   */
  term_years: number | null;
  /** The last day cover runs, or null. The operator's date when they set one. */
  expiry_date: string | null;
  /** §9.1, NET of discount. */
  line_total: string;
}

/** `QuoteTaxRowDto` — `quotes.service.ts:227`. D6: a quote has exactly one. */
export interface QuoteTaxRowDto {
  id: string;
  quote_id: string;
  country_id: number;
  tax_name: string;
  rate: string;
  base_amount: string;
  tax_amount: string;
}

export interface QuoteRecordDto extends QuoteDto {
  lines: QuoteLineRowDto[];
  taxes: QuoteTaxRowDto[];
}

/** `PreviewLineDto` — `quotes.service.ts:312`. */
export interface PreviewLineDto {
  line_no: number;
  product_id: string;
  product_code: string;
  description: string;
  qty: string;
  users: number | null;
  months: number | null;
  unit_rate: string;  discount_pct: string;
  /**
   * The renewal term recorded on this line, or null for a product not sold on a term.
   *
   * SERVER-DECIDED, and echoed here so the builder can show the operator the prefill it
   * is about to store. Neither field is money; both are recorded in the API's
   * money-field audit as deliberate non-entries.
   */
  term_years: number | null;
  /** The last day cover runs, or null. The operator's date when they set one. */
  expiry_date: string | null;
  line_total: string;
}

/** `PreviewTaxDto` — `quotes.service.ts:325`. */
export interface PreviewTaxDto {
  tax_name: string;
  rate: string;
  base_amount: string;
  tax_amount: string;
}

/**
 * C2's reason vocabulary — a copy of the server's `StateUnresolvedReason`
 * (`apps/api/src/modules/tax/tax.service.ts`), copied rather than imported for
 * the standing reason: the browser bundle does not import NestJS modules.
 */
export type StateUnresolvedReason =
  | 'PARTY_STATE_UNSET'
  | 'SELLER_STATE_UNSET'
  | 'PARTY_AND_SELLER_STATE_UNSET';

/**
 * `QuotePreviewDto` — `quotes.service.ts:290`. The full computed envelope,
 * persisted nowhere.
 *
 * It carries `taxable_base`, which the RECORD does not: it is §9.1's own
 * intermediate and the preview is the diagnostic surface where showing it is the
 * point. The builder renders it for exactly that reason — it is the number
 * `grand_total` was built FROM, so a reader can see the pipeline rather than
 * being asked to trust it.
 */
export interface QuotePreviewDto {
  country_id: number;
  country_code: string;
  doc_date: string;
  party_id: string;
  currency: string;
  fx_rate_to_inr: string;
  fx_stale: boolean;
  fx_period: string | null;
  /**
   * The tax GROUP this preview priced under (migration 30, ruling OD-1) — the
   * country default until the picker lands (design U3).
   */
  tax_group_id: string;
  tax_group_name: string;
  /**
   * OD-2/S8 — the India split ladder could not resolve the party's state and
   * defaulted to the IGST branch. NON-BLOCKING by ruling; the builder renders
   * the hint ("state unresolved — showing IGST") from this flag.
   */
  tax_state_unresolved: boolean;
  /**
   * C2 (owner ruling 2026-08-26, `docs/OQ-GST-NULL-HOME-STATE.md` §6) — WHICH
   * state is unset, so the hint names the RIGHT record. VERBATIM: *"A hint that
   * tells a coordinator to fix the party when the seller's own state is missing
   * sends them to the wrong screen, and they will not find the problem there."*
   *
   *   · `PARTY_STATE_UNSET`            → the fix is the PARTY record;
   *   · `SELLER_STATE_UNSET`           → the fix is the COUNTRY's own state
   *     (the record behind Country settings) — NOT any party;
   *   · `PARTY_AND_SELLER_STATE_UNSET` → both records, named together so neither
   *     hides behind the other (no precedence — see the server type's rationale).
   *
   * `null` iff `tax_state_unresolved` is false. The boolean stays the render
   * GATE; this only picks the copy — additive wire, no pre-C2 consumer moves.
   * Mirrors `StateUnresolvedReason` in `apps/api/src/modules/tax/tax.service.ts`.
   */
  tax_state_unresolved_reason: StateUnresolvedReason | null;
  lines: PreviewLineDto[];
  taxes: PreviewTaxDto[];
  totals: {
    subtotal: string;
    discount_total: string;
    taxable_base: string;
    tax_total: string;
    grand_total: string;
  };
}

/**
 * A line as the CLIENT may express it — `QuoteLineDto` in
 * `apps/api/src/modules/quotes/dto/quotes.dto.ts`.
 *
 * THE ABSENCE IS THE CONTRACT — and since the Phase 3a close-out (T0, OQ-1 void)
 * it is ONE absence, not two. `unit_rate` is now a client field: CRF is
 * internal-only, the operator knows the agreed price and types it. `line_total`
 * is still missing and must stay missing, because it is DERIVED — §9.1 computes
 * `qty × unit_rate × (1 − discount_pct/100)` on the server and a client that
 * names it has named the total. The global `ValidationPipe` runs with
 * `forbidNonWhitelisted`, so a body carrying `line_total` is REJECTED rather than
 * stripped; this type is the first line of that defence and the reason the
 * interface is written out longhand instead of derived from `PreviewLineDto`.
 */
export interface QuoteLineInput {
  product_id: string;
  /** DECIMAL(10,2) as a string. Over-scale input is refused by the API, not rounded. */
  qty: string;
  /**
   * DECIMAL(14,2) as a string — the rate the operator agreed. OMIT IT and the
   * server prices the line from `country_products.price`, which is what every
   * caller did before this field existed.
   */
  unit_rate?: string | null;
  users?: number | null;
  months?: number | null;
  /** DECIMAL(6,3) as a string. The ASK; what it is worth is the server's answer. */
  discount_pct?: string | null;
  description?: string | null;
  /**
   * Renewal term in whole years — 1 or 2, for a term-bearing product only (migration 18).
   *
   * OMIT IT and the server prefills one year. Sending it for a product the catalogue does
   * not mark term-bearing is a 422 naming the product, not a silent drop.
   */
  term_years?: number | null;
  /**
   * The FIRST day cover runs, 'YYYY-MM-DD' (migration 25). OMIT IT and the server prefills
   * the sale date.
   *
   * ⚠ IT OVERRIDES INDEPENDENTLY OF `expiry_date`, and the server is built that way:
   * `effectiveTerm` is `line.term_start ?? saleDate` and
   * `line.expiry_date ?? expiryFor(saleDate, term)` — two separate `??`, neither derived
   * from the other. A client that computed one from the other would make the pair the API
   * exists to express — a renewal sold in November to START on 1 January — unreachable.
   */
  term_start?: string | null;
  /**
   * The last day cover runs, 'YYYY-MM-DD'. OMIT IT and the server derives it from the
   * sale date and the term.
   *
   * ⚠ A VALUE HERE IS THE TRUTH AND IS STORED AS TYPED. Nothing on the server checks it
   * against the derived date — renewal offers give thirteen months for twelve, and a
   * check would fail exactly those. Do not "helpfully" recompute it in the client either.
   */
  expiry_date?: string | null;
}

/** `CreateQuoteDto` — one class for BOTH `preview` and `create`, on purpose. */
export interface QuoteInput {
  party_id: string;
  /**
   * ⚠ THE BUILDER NO LONGER SENDS THIS, AND THE FIELD IS KEPT ONLY TO SAY SO
   * (C11/IQ-12b, decision D-IQ-1).
   *
   * It used to read: *"Omitted means `'crf'` — the API defaults it … Settable at CREATE
   * only."* The server does not default it any more; it DERIVES it from the lines. A body
   * that still carries the field is treated as an ASSERTION rather than a choice.
   *
   * ⚠ WHAT THE SERVER DOES WITH THAT ASSERTION CHANGED AGAIN, AND THIS COMMENT SAID THE
   * OLD ANSWER. It read *"answered 422 when it disagrees … which is what a released client
   * sending `type: 'tally_sales'` for an Angolan Tally document will now get"*. That is no
   * longer what a released client gets: the API carries a STALE-CLIENT TOLERANCE (owner
   * ruling 1, `apps/api/src/common/client-compat.ts`) which defaults ON, and while it is on
   * a disagreeing claim is IGNORED and the create proceeds on the derived type. The 422 is
   * what `CRF_STALE_CLIENT_TOLERANCE=0` restores.
   *
   * ⚠ THE CLIENT THE TOLERANCE IS FOR IS THIS FILE'S OWN PREDECESSOR. At `ef68f2c` the
   * builder still set `type` on both routes; the tolerance is what keeps a browser holding
   * that bundle saving during the gap between the API deploy and the web deploy. Nothing in
   * this app sets it any more — `toRequest` stopped, and the type is displayed rather than
   * chosen — so this app is not the one being tolerated. The field survives on the wire
   * type only because the API still accepts it (removing it from the server DTO would 400
   * every released client at the pipe).
   */
  type?: QuoteType;
  inquiry_id?: string | null;
  legacy_ref?: string | null;
  valid_till?: string | null;
  /**
   * The picked tax group (migration 30, OD-1 — design U3). OMIT IT and the server
   * prices under the COUNTRY DEFAULT, exactly as every caller did before the field
   * existed — which is why the builder sends it only when the operator picked one.
   * A retired group is refused as a NEW pick (M6, 422 `TAX_GROUP_RETIRED`).
   */
  tax_group_id?: string | null;
  /**
   * WHICH OF THE COUNTRY'S PRICE LISTS THIS QUOTE IS IN — chosen by the salesperson before
   * the first line (owner, 2026-09-22: *"before generating any quote for CD user has to
   * select the option in which currency they are to create quote in"*).
   *
   * Sent on preview AND create, always — the builder reads the country's currencies from
   * {@link fetchQuoteCurrencies} and sends the one chosen (or, in a one-currency country,
   * the only one). The server refuses a two-currency country's quote without it, and any
   * value outside the country's set. Fixed once saved: an update carrying it is a 400.
   */
  currency?: string;
  lines: QuoteLineInput[];
}

/** `MAX_QUOTE_LINES` in `dto/quotes.dto.ts`. Over this the API answers 400. */
export const MAX_QUOTE_LINES = 200;

/* ───────────────────────────────────────────────────────────────── quotes ── */

export async function fetchQuotes(
  countryCode: string,
  params: { status?: string; page: number; perPage: number },
  signal?: AbortSignal,
): Promise<Paged<QuoteDto>> {
  return apiRequest<Paged<QuoteDto>>(`/countries/${countryCode}/quotes`, {
    query: { status: params.status, page: params.page, perPage: params.perPage },
    signal,
  });
}

export async function fetchQuote(id: string, signal?: AbortSignal): Promise<QuoteRecordDto> {
  return apiRequest<QuoteRecordDto>(`/quotes/${id}`, { signal });
}

/**
 * `POST /countries/:cc/quotes/preview` — SERVER-PRICED, PERSISTS NOTHING.
 *
 * This is the only way the builder learns a number. It is called on every change
 * (debounced) and its response is rendered verbatim.
 *
 * It can answer **501 `NOT_IMPLEMENTED_PENDING_DECISION`** for a line whose
 * product is not `pricing_model: 'flat'` (OQ-1). That is not an error to hide: it
 * is the API refusing to guess a subscription price, and the builder surfaces it
 * per line. See {@link unpriceableProductIds}.
 */
export async function previewQuote(
  countryCode: string,
  body: QuoteInput,
  signal?: AbortSignal,
): Promise<QuotePreviewDto> {
  return apiRequest<QuotePreviewDto>(`/countries/${countryCode}/quotes/preview`, {
    method: 'POST',
    body,
    signal,
  });
}

export async function createQuote(
  countryCode: string,
  body: QuoteInput,
): Promise<QuoteRecordDto> {
  return apiRequest<QuoteRecordDto>(`/countries/${countryCode}/quotes`, {
    method: 'POST',
    body,
  });
}

/**
 * The ORIGINAL a recovery document integrates (AGT U7; DE 74/19 8, 9) — its identifiers in
 * SEPARATE fields (8 c), 9 f)); the server composes the citation (`ab/0001`, a D's `OR AOQT2026/7`).
 */
export interface RecoveryInput {
  recovery_kind: 'M' | 'D';
  /** `D` only: the duplicate's document type code — `OR` (a quotation). */
  recovery_type_code?: string;
  /** The original's série as printed, e.g. `ab` (no space or `/`, ≤ 40). */
  recovery_serie: string;
  /** The original's number as printed, digits, e.g. `0001` (≤ 19). */
  recovery_number: string;
  /** The original's date, `YYYY-MM-DD`, not after today. */
  recovery_date: string;
  /** 33 c): the operator confirmed the server's clock after a `CLOCK_BEHIND_LAST_DOCUMENT` refusal. */
  confirm_clock?: boolean;
}

/**
 * `POST /countries/:cc/quotes/recover` — perms `quotes.create` + `config.manage`, AGT regime
 * only. The create body plus the original; the server prices, numbers it in the recovery série,
 * SENDS and signs it in one step, and answers with the sent record.
 */
export async function recoverQuote(
  countryCode: string,
  body: QuoteInput & RecoveryInput,
): Promise<QuoteRecordDto> {
  return apiRequest<QuoteRecordDto>(`/countries/${countryCode}/quotes/recover`, {
    method: 'POST',
    body,
  });
}

/**
 * `PATCH /quotes/:id` — the CLOSED field set: `legacy_ref`, `valid_till`, `lines`.
 *
 * No `status`. The lifecycle routes below are the only thing that moves it, and
 * the DTO's own header explains at length why a PATCH that could write it would
 * be a confirmed sale with no sales order behind it.
 *
 * `lines` is refused on a non-draft quote (decision U1-9, 422).
 */
export async function patchQuote(
  id: string,
  body: {
    legacy_ref?: string | null;
    valid_till?: string | null;
    lines?: QuoteLineInput[];
    /**
     * Re-point a DRAFT at another tax group (M5): the server re-prices even without
     * `lines` — the stored lines are the pricing input, the tax alone re-resolves.
     * No screen sends it yet; declared so the contract mirror stays whole.
     */
    tax_group_id?: string | null;
  },
): Promise<QuoteRecordDto> {
  return apiRequest<QuoteRecordDto>(`/quotes/${id}`, { method: 'PATCH', body });
}

/* ────────────────────────────────────────────────────────────── lifecycle ── */

/**
 * `POST /quotes/:id/send` — `draft → sent`, perm `quotes.send`.
 *
 * `channel` is the actor's DECLARATION of how they delivered the document, not an
 * instruction to deliver it: Phase 3a sends no email and posts to no WhatsApp
 * (OQ-U3-2). It is recorded in the `quote.sent` event payload and nowhere else,
 * because `quotes` has no channel column and this phase may not add one.
 *
 * `confirm_clock: true` — the operator's answer to a 409 `CLOCK_BEHIND_LAST_DOCUMENT` (DE 74/19
 * 33 c), AGT U2): the server's clock is right, issue the document anyway. Same on `/confirm`.
 */
export async function sendQuote(
  id: string,
  body: { channel?: 'email' | 'whatsapp_share' | null; confirm_clock?: true } = {},
): Promise<QuoteRecordDto> {
  return apiRequest<QuoteRecordDto>(`/quotes/${id}/send`, { method: 'POST', body });
}

/** What `POST /quotes/:id/confirm` answers (`QuoteConfirmationDto`). */
export interface QuoteConfirmationDto {
  quote: QuoteRecordDto;
  /*
   * NULL FOR A TALLY SALES QUOTE (§B5). Confirming a CRF quote creates the sales
   * order that carries the work forward; what confirming a Tally Sales quote
   * should do is the owner's open question (OQ-TS-AUTOCONFIRM), so the server
   * makes the transition and stops. The null is that decision left open, and the
   * UI must state it rather than paper over it — see `QuoteRecordPage`'s confirm
   * handler, which chooses its toast on this field.
   *
   * FIELD LIST CORRECTED while adding the null: the server sends `country_id` and
   * has never sent `tax_names` (`QuoteConfirmationDto` in
   * `quote-lifecycle.service.ts`). Nothing read `tax_names`, so nothing broke —
   * but a mirror that names a field the wire does not carry is a lie waiting for
   * its first reader.
   */
  sales_order: {
    id: string;
    doc_no: string;
    country_id: number;
    status: SalesOrderStatus;
    lines: number;
  } | null;
}

/**
 * `POST /quotes/:id/confirm` — `sent → confirmed`, creating the sales order at
 * `pending_push` AND STOPPING THERE. Phase 3b owns the Tally push. No body:
 * everything the order needs is already on the quote, and a body would be
 * somewhere for a client to put a number — save `confirm_clock` (AGT U2, 33 c), see
 * {@link sendQuote}), sent only when the operator has confirmed the clock.
 */
export async function confirmQuote(
  id: string,
  body?: { confirm_clock: true },
): Promise<QuoteConfirmationDto> {
  return apiRequest<QuoteConfirmationDto>(`/quotes/${id}/confirm`, { method: 'POST', body });
}

/**
 * `POST /quotes/:id/lost {reason}` — MANDATORY reason, 422 `LOST_REASON_REQUIRED`.
 *
 * OQ-U3-3: `quotes` has no `lost_reason` column, so the reason is stored in the
 * `quote.lost` event payload only. The record page states that rather than
 * showing an empty field where a reason should be.
 */
export async function markQuoteLost(id: string, reason: string): Promise<QuoteRecordDto> {
  return apiRequest<QuoteRecordDto>(`/quotes/${id}/lost`, {
    method: 'POST',
    body: { reason },
  });
}

/**
 * `POST /quotes/:id/annul {reason}` — `sent → annulled`, SAF-T(AO) countries only, perms
 * `quotes.confirm` + `config.manage` (AGT design §3 U2, D7). Reason mandatory, ≤ 50.
 */
export async function annulQuote(id: string, reason: string): Promise<QuoteRecordDto> {
  return apiRequest<QuoteRecordDto>(`/quotes/${id}/annul`, {
    method: 'POST',
    body: { reason },
  });
}

/* ──────────────────────────────────────────────────────────────── discounts ── */

/** `DiscountRequestRowDto` — `quote-discounts.service.ts:255`. */
export interface DiscountRequestDto {
  id: string;
  quote_id: string;
  country_id: number;
  requested_by: string;
  /** DECIMAL(6,3); null when the ask was an amount. */
  requested_pct: string | null;
  /** DECIMAL(14,2); null when the ask was a percentage. Masked without `amounts.view`. */
  requested_amount: string | null;
  reason: string;
  status: DiscountRequestStatus;
  decided_by: string | null;
  decided_at: string | null;
  decision_note: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface DiscountDecisionResultDto {
  request: DiscountRequestDto;
  quote: QuoteRecordDto;
}

/**
 * `POST /quotes/:id/discount-request` — perm `quotes.discount.request`.
 *
 * TWO API LIMITS THE UI MUST NOT PAPER OVER:
 *  · OQ-6 — only a DRAFT may carry a request. The real business flow (a customer
 *    pushes back on a quote they have already been SENT) does not work today, so
 *    the record page offers this action on drafts only and says why.
 *  · OQ-4 — an AMOUNT ask is accepted and stored but cannot be APPROVED (501).
 *    The form therefore takes a PERCENTAGE, and names the amount limitation
 *    rather than offering a field whose happy path does not exist.
 */
export async function requestDiscount(
  id: string,
  body: { pct: string; reason: string },
): Promise<DiscountRequestDto> {
  return apiRequest<DiscountRequestDto>(`/quotes/${id}/discount-request`, {
    method: 'POST',
    body,
  });
}

/**
 * `POST /quotes/:id/discount-decision` — perm `quotes.discount.approve`.
 *
 * The approver may NOT be the requester (403). On approve the quote is re-priced
 * through the same pipeline that priced it, and the response carries the quote as
 * it now stands — which is why nothing here recomputes a total after a decision.
 */
export async function decideDiscount(
  id: string,
  body: { decision: 'approve' | 'reject'; decision_note?: string | null },
): Promise<DiscountDecisionResultDto> {
  return apiRequest<DiscountDecisionResultDto>(`/quotes/${id}/discount-decision`, {
    method: 'POST',
    body,
  });
}

/* ────────────────────────────────────────────────────────────────────── PDF ── */

/** `QuotePdfRenderedView` — `quote-pdf.service.ts:174`. */
export interface QuotePdfRenderedDto {
  ok: true;
  pdf_url: string;
  bytes: number;
  /** True when the document was stamped DRAFT (keyed on `sent_at`, decision U4-3). */
  watermarked: boolean;
}

/** `POST /quotes/:id/pdf` — renders, stores `pdf_path`, returns the URL. Rate limited. */
export async function renderQuotePdf(id: string): Promise<QuotePdfRenderedDto> {
  return apiRequest<QuotePdfRenderedDto>(`/quotes/${id}/pdf`, { method: 'POST' });
}

/**
 * `GET /quotes/:id/pdf` — the AUTH-CHECKED stream.
 *
 * A plain `<a href="/api/v1/quotes/7/pdf">` would 401: the route is behind the JWT
 * guard and an anchor carries no `Authorization` header. So the bytes come through
 * the api client (which also keeps the request inside the refresh mutex) and the
 * caller hands them to the browser as an object URL — the same shape
 * `features/avatars/use-avatar.ts` uses for a photo, which is the precedent this
 * follows rather than a new mechanism.
 */
export async function fetchQuotePdf(id: string): Promise<BinaryResponse> {
  return fetchBinary(`/quotes/${id}/pdf`);
}

/* ─────────────────────────────────────────────────────────── sales orders ── */

/** `SalesOrderDto` — `apps/api/src/modules/sales-orders/dto/sales-orders.dto.ts`. */
export interface SalesOrderDto {
  id: string;
  doc_no: string;
  country_id: number;
  quote_id: string;
  quote_doc_no: string | null;
  party_id: string;
  party_name: string | null;
  /**
   * §B3 — the CUSTOMER's country. NULL for the five domestic scopes (it would repeat
   * the document's own country); non-null for XM, where it is the only thing separating
   * a Kenyan customer from an Omani one. Rendered by `customerOriginLabel`.
   */
  party_customer_country?: string | null;
  status: SalesOrderStatus;
  currency: string;
  fx_rate_to_inr: string;
  fx_stale: boolean;
  subtotal: string;
  discount_total: string;
  tax_total: string;
  grand_total: string;
  /**
   * The snapshot-copied tax-group provenance (migration 30, OD-1 — design U3), with
   * its label joined live. Null on orders confirmed before the cutover. Not money.
   */
  tax_group_id: string | null;
  tax_group_name: string | null;
  /** Written by the Phase 3b push. Always null in 3a. */
  tally_voucher_no: string | null;
  tally_pushed_at: string | null;
  invoice_no: string | null;
  invoice_date: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface SoLineRowDto {
  id: string;
  sales_order_id: string;
  country_id: number;
  line_no: number;
  product_id: string;
  product_code: string | null;
  description: string;
  qty: string;
  users: number | null;
  months: number | null;
  unit_rate: string;  discount_pct: string;
  /**
   * The renewal term recorded on this line, or null for a product not sold on a term.
   *
   * SERVER-DECIDED, and echoed here so the builder can show the operator the prefill it
   * is about to store. Neither field is money; both are recorded in the API's
   * money-field audit as deliberate non-entries.
   */
  term_years: number | null;
  /** The last day cover runs, or null. The operator's date when they set one. */
  expiry_date: string | null;
  line_total: string;
}

export interface SoTaxRowDto {
  id: string;
  sales_order_id: string;
  country_id: number;
  tax_name: string;
  rate: string;
  base_amount: string;
  tax_amount: string;
}

export interface SalesOrderRecordDto extends SalesOrderDto {
  lines: SoLineRowDto[];
  taxes: SoTaxRowDto[];
}

export async function fetchSalesOrders(
  countryCode: string,
  params: { status?: string; page: number; perPage: number },
  signal?: AbortSignal,
): Promise<Paged<SalesOrderDto>> {
  return apiRequest<Paged<SalesOrderDto>>(`/countries/${countryCode}/sales-orders`, {
    query: { status: params.status, page: params.page, perPage: params.perPage },
    signal,
  });
}

export async function fetchSalesOrder(
  id: string,
  signal?: AbortSignal,
): Promise<SalesOrderRecordDto> {
  return apiRequest<SalesOrderRecordDto>(`/sales-orders/${id}`, { signal });
}

/* ──────────────────────────────────────────────────────────────── catalogue ── */

/** `CountryProductDto` — `catalog-stub/country-products.service.ts:39`. */
/** `products.kind` — the B2 classification (migration 14). */
export const PRODUCT_KINDS = ['crf_service', 'tally_licence', 'gov_patch'] as const;
export type ProductKind = (typeof PRODUCT_KINDS)[number];

export interface CountryProductDto {
  country_id: number;
  product_id: string;
  product_code: string;
  product_name: string;
  /**
   * WHICH CLASS OF PRODUCT — carried on this route precisely so the builder can show
   * Angola's mandatory patch as a REQUIRED line instead of letting the operator find
   * out through a 422 at save (§B2, §N5). The server still enforces it; this is what
   * makes the requirement visible before the request is sent.
   */
  kind: ProductKind;
  /**
   * TRUE while this country price row is still marked "not yet reviewed" (migration 15).
   *
   * ⚠ INFORMATIONAL ONLY SINCE G1 (tax-groups design §5): the server no longer refuses
   * anything over it — the PRICE_UNVERIFIED gate and the builder's per-line warning
   * are both retired. The field stays on the wire because the admin pricing screen
   * still surfaces it, and because removing a shipped response field is a contract
   * break this ruling does not require.
   *
   * ⚠ NOT A MONEY FIELD. It says a row awaits review and carries no amount, so it is
   * not in `MONEY_FIELDS` and is not masked.
   */
  verify_before_prod: boolean;
  /**
   * The catalogue rate. A HINT for the picker — never a number that reaches a total.
   * `null` when {@link priced_per_customer} (and `•••` when masked).
   */
  price: string | null;
  /**
   * The list's price is deliberately BLANK — priced per customer, typed on every quote
   * (migration 66, owner ruling R3: the government patch). Not money and never masked, so
   * the screen reads the blank from this flag in every seat rather than inferring it from
   * `price`, the one field whose value depends on who is asking.
   */
  priced_per_customer: boolean;
  /** Which of the country's lists this row is in (migration 66). */
  currency: string;
  active: boolean;
  /*
   * ──────────────────────────────────────────────────────────────────────────
   * MIGRATION 17 — prices are EFFECTIVE-DATED. These four arrive on every
   * catalogue read and this transcription went stale the moment they shipped.
   * ──────────────────────────────────────────────────────────────────────────
   * A transcribed DTO that silently lags the server is worse than no
   * transcription: it type-checks, so nothing complains, and the fields simply
   * become invisible to every screen that reads through it.
   *
   * None of the four is money. Each says WHEN or WHETHER a price applies, and
   * all four are recorded as deliberate non-entries in the API's money-field
   * audit — a seat without `amounts.view` still gets `•••` for `price` beside a
   * perfectly readable calendar, which is the correct pairing.
   */
  /**
   * `products.term_bearing` — this product is sold for a fixed TERM (migration 18), so
   * its quote lines carry a term and an expiry date.
   *
   * Carried for the same reason as `kind` above: this route is the builder's line-item
   * picker, so it is the only place the client can learn that a line needs those
   * controls. The server still decides and still enforces; this makes it showable.
   */
  term_bearing: boolean;
  /**
   * The shelf this product is filed on, or `null` — U3.4, migration 23.
   *
   * SAME ROUTE, DIFFERENT REASON FROM `kind` AND `term_bearing` ABOVE. Those are here so the
   * picker can SHOW a rule before it fires. This is here so the picker can show a HEADING,
   * and that is the whole of what it does — it reaches no rule and can cause no refusal.
   * If this field ever starts deciding something, the server's own measurement suite
   * (`product-groups.e2e-spec.ts`) is what fails first.
   *
   * The NAME as well as the id, so the `<optgroup>` needs no second request and no
   * client-side join against a separately-fetched list.
   */
  product_group_id: string | null;
  product_group_name: string | null;
  /**
   * The shelf's own display order — `null` for an ungrouped product, which sorts LAST.
   *
   * ⚠ WITHOUT IT THE PICKER SHOWS ONE SHELF TWICE. `useCatalogue` sorts by product code, so
   * a catalogue with TP-SILVER on "Addon", TSS unfiled and TSS-1Y on "Item" produces the
   * headings Addon · Ungrouped · Item — measured, not imagined. Shelf order is the
   * country's decision, so it travels with the row rather than being guessed alphabetically.
   */
  product_group_sort: number | null;
  /** First day this price applies, inclusive, 'YYYY-MM-DD'. */
  effective_from: string;
  /** Last day it applies, inclusive; `null` while still in force. */
  effective_to: string | null;
  /**
   * Another version of THIS PRODUCT starts after the date the row was resolved
   * against — "a change is queued". A boolean, never the future amount.
   */
  has_future_price: boolean;
  /**
   * Where THIS ROW's window sits relative to that date.
   *
   * Deliberately separate from `has_future_price`: one is about the product,
   * the other about the row, and the server carried a single field meaning both
   * until it was split. `GET /countries/:cc/products` only ever returns
   * `current`; the history route returns all three.
   */
  window_state: PriceWindowState;
}

/** Past, in force, or not started yet. Mirrors the API's `PriceWindowState`. */
export const PRICE_WINDOW_STATES = ['past', 'current', 'future'] as const;
export type PriceWindowState = (typeof PRICE_WINDOW_STATES)[number];

/** `ProductDto` — `catalog-stub/products.service.ts:32`. */
export interface ProductDto {
  id: string;
  code: string;
  name: string;
  category: string | null;
  /** `flat | per_user_month | slab`. Only `flat` can be priced today — OQ-1. */
  pricing_model: string;
  meta: Record<string, unknown> | null;
  active: boolean;
}

/**
 * `GET /countries/:cc/products` — what this country sells, and for how much.
 *
 * Country scope is the ONLY gate (`@CountryScoped()`, no `@RequirePerm`): quoting
 * needs the catalogue, managing it needs `config.manage`. Inactive rows are
 * excluded by default and this app never asks for them — a product deactivated in
 * a country cannot be quoted there, so listing it would be offering a line the
 * pricing service will refuse.
 */
export async function fetchCountryProducts(
  countryCode: string,
  signal?: AbortSignal,
  /**
   * WHICH LIST (migration 66). Omitted means the country's HOME currency — the server's
   * default, and exactly the answer every caller written before this change relied on.
   * LAST, so those positional callers (`RenewalsPage`) still compile.
   */
  currency?: string,
): Promise<CountryProductDto[]> {
  const body = await apiRequest<{ data: CountryProductDto[] }>(
    `/countries/${countryCode}/products`,
    { signal, query: currency === undefined ? undefined : { currency } },
  );
  return body.data;
}

/** `GET /countries/:cc/products/currencies` — the country's quote currencies, home first. */
export interface QuoteCurrenciesDto {
  currencies: string[];
  /** TRUE when there is more than one — the salesperson must choose before the first line. */
  choice_required: boolean;
}

/**
 * The currencies a quote in this country may be in (2026-09-22). Country scope only, like the
 * catalogue: every quoting seat reads it, none needs `config.manage`.
 */
export async function fetchQuoteCurrencies(
  countryCode: string,
  signal?: AbortSignal,
): Promise<QuoteCurrenciesDto> {
  const body = await apiRequest<{ data: QuoteCurrenciesDto }>(
    `/countries/${countryCode}/products/currencies`,
    { signal },
  );
  return body.data;
}

/**
 * `GET /products` — the GLOBAL master, fetched for exactly ONE field:
 * `pricing_model`.
 *
 * `country_products` does not carry it. It no longer decides whether a line can
 * be priced — OQ-1 is void and every catalogue product prices — and it still
 * decides ONE thing: whether the line editor offers the users/months capture
 * fields, which are worth asking for on a subscription and noise on a licence.
 *
 * Readable by any authenticated caller — "the row carries no money"
 * (`catalog.controller.ts`). No permission is being leaned on.
 */
export async function fetchProductModels(signal?: AbortSignal): Promise<ProductDto[]> {
  const body = await apiRequest<Paged<ProductDto>>('/products', {
    query: { active: true, page: 1, perPage: 200 },
    signal,
  });
  return body.data;
}

/* ─────────────────────────────────────────────────────────────── tax groups ── */

/** `TaxGroupDto` — `countries/tax-groups.service.ts`. Names and flags; no money. */
export interface TaxGroupDto {
  id: string;
  country_id: number;
  name: string;
  /** `'NONE' | 'IN_GST_STATE_SPLIT'` — carried for completeness; the builder shows names. */
  split_mode: string;
  /** At most one per country — the server's NULL-unique key is the floor. */
  is_default: boolean;
  active: boolean;
  created_at: string | null;
}

/**
 * `GET /countries/:cc/tax-groups` — the PICKER list (design U3).
 *
 * Scope is the only gate, like the product picker: the people who pick tax groups
 * are the people who quote. Active groups only — retirement removes a group from
 * this list while documents that already carry it keep resolving (M6).
 */
export async function fetchTaxGroups(
  countryCode: string,
  signal?: AbortSignal,
): Promise<TaxGroupDto[]> {
  const body = await apiRequest<{ data: TaxGroupDto[] }>(
    `/countries/${countryCode}/tax-groups`,
    { signal },
  );
  return body.data;
}

/* ────────────────────────────────────────────────────────────── party search ── */

/**
 * Party typeahead — the same route and the same cap the New Inquiry panel uses
 * (`features/inquiries/api.ts`), re-declared here rather than imported so the
 * quotes feature does not reach into B4's module for a URL.
 *
 * The SHAPE is imported, because two declarations of `PartyDto` is how the two
 * screens end up disagreeing about what a party is.
 */
export type { PartyDto } from '../inquiries/api';

/* ─────────────────────────────────────────────────────────────── query keys ── */

export const quoteKeys = {
  list: (cc: string, params: Record<string, unknown>) => ['quotes', cc, 'list', params] as const,
  counts: (cc: string) => ['quotes', cc, 'counts'] as const,
  record: (id: string) => ['quote', id] as const,
  /**
   * The preview key carries the whole request body. That is deliberate: a preview
   * IS its inputs, so two different line sets must never share a cache entry —
   * the failure mode being a builder that shows yesterday's total for today's
   * basket, which is precisely the class of lie this unit exists to prevent.
   */
  preview: (cc: string, body: unknown) => ['quotes', cc, 'preview', body] as const,
  /** Per LIST since migration 66 — a CDF catalogue and a USD one are different answers. */
  catalogue: (cc: string, currency: string) => ['country-products', cc, currency] as const,
  currencies: (cc: string) => ['country-products', cc, 'currencies'] as const,
  productModels: () => ['products', 'models'] as const,
  /** The pickable tax groups (U3) — admin edits are rare; see `useTaxGroups`' staleTime. */
  taxGroups: (cc: string) => ['tax-groups', cc] as const,
  partySearch: (cc: string, q: string, includeHistory = false) =>
    ['parties', cc, 'search', q, includeHistory] as const,
};

export const salesOrderKeys = {
  list: (cc: string, params: Record<string, unknown>) =>
    ['sales-orders', cc, 'list', params] as const,
  counts: (cc: string) => ['sales-orders', cc, 'counts'] as const,
  record: (id: string) => ['sales-order', id] as const,
};
